/**
 * Values of a new request that can be preset via URL parameters, e.g. by a page that embeds the advisor:
 * ?interesse=massnahme&massnahmen=heating,fassade&plz=10115&step=chapter1
 */
export interface EinstiegPrefill {
  interesse: string;
  interesseMassnahmen: string | null;
  postleitzahl: string | null;
}

/**
 * Reads the prefill values from the URL parameters "interesse", "massnahmen" and "plz". Unknown interests and
 * measures are ignored. Without an interest, "massnahme" is used if there are measures, "alles" otherwise. The
 * interest "massnahme" without any known measure falls back to "alles".
 *
 * Example: getEinstiegPrefill({massnahmen: 'heating,pv'}, ['alles', 'massnahme'], ['heating', 'dach'])
 * returns {interesse: 'massnahme', interesseMassnahmen: 'heating', postleitzahl: null}
 *
 * @param params the query parameters
 * @param interestList the interests that can be selected
 * @param measureList the measures that can be selected for the interest "massnahme"
 * @return the prefill values or null, if no prefill parameter is set
 */
export function getEinstiegPrefill(params: Record<string, unknown>, interestList: string[], measureList: string[]): EinstiegPrefill | null {
  const interesseParam = typeof params?.interesse === 'string' ? params.interesse : null;
  const massnahmenParam = typeof params?.massnahmen === 'string' ? params.massnahmen : null;
  const plzParam = typeof params?.plz === 'string' ? params.plz.trim() : null;

  if (interesseParam === null && massnahmenParam === null && plzParam === null) {
    return null;
  }

  const measures = (massnahmenParam ?? '')
    .split(',')
    .map(measure => measure.trim())
    .filter((measure, index, list) => measureList.includes(measure) && list.indexOf(measure) === index);

  let interesse = interesseParam !== null && interestList.includes(interesseParam) ? interesseParam : null;
  if (interesse === null) {
    interesse = measures.length > 0 ? 'massnahme' : 'alles';
  }
  if (interesse === 'massnahme' && measures.length === 0) {
    interesse = 'alles';
  }

  return {
    interesse,
    interesseMassnahmen: interesse === 'massnahme' ? measures.join(',') : null,
    postleitzahl: plzParam !== null && /^\d{5}$/.test(plzParam) ? plzParam : null,
  };
}
